import ExcelJS from "exceljs";
import { displayContractBasis } from "@/utils/contractBasis";
import { saveAs } from "file-saver";
import { format, parseISO } from "date-fns";
import type {
  Client,
  ClientContact,
  ClaimsSummaryRow,
  ExportOptions,
  Policy,
  Reimbursement,
  StoplossClaim,
  Task,
  User,
  Vendor,
  WatchListRow,
} from "@/types";
import { UNASSIGNED_VENDOR_KEY } from "../utils/calculateClaimsSummaryRows";
import { paymentTypeLabel } from "../utils/paymentTypeLabel";
import { reimbursementDate } from "../utils/reportingPeriod";
import { isAggregateSpecificPolicy } from "../utils/aggregateSpecific";

// ==================== SHARED CONSTANTS ====================

/**
 * Orders the per-vendor filed columns (#5326): policy-listed vendors first in
 * the policy's order, then any stray vendors, then the Unassigned bucket last.
 * Only vendors that actually have filed amounts get a column (data-driven).
 */
const orderedVendorColumns = (
  rows: ClaimsSummaryRow[],
  pick: (r: ClaimsSummaryRow) => Record<string, number>,
  policyOrder: string[]
): string[] => {
  const present = new Set<string>();
  rows.forEach((r) => Object.keys(pick(r) ?? {}).forEach((k) => present.add(k)));
  const ordered: string[] = [];
  policyOrder.forEach((id) => {
    if (present.has(id)) {
      ordered.push(id);
      present.delete(id);
    }
  });
  [...present].filter((k) => k !== UNASSIGNED_VENDOR_KEY).forEach((k) => ordered.push(k));
  if (present.has(UNASSIGNED_VENDOR_KEY)) ordered.push(UNASSIGNED_VENDOR_KEY);
  return ordered;
};

// resolveVendorName returns a vendor's display name, or "Unknown (<short id>)" for an id
// absent from the policy's loaded vendor list (e.g. a vendor removed from the policy after a
// run-out cutover). The short-id suffix keeps distinct off-policy vendors distinguishable so
// they don't all collapse onto an ambiguous "Unknown" column/label (#5326 review).
const resolveVendorName = (vendorLabelById: Map<string, string>, vendorId: string): string =>
  vendorLabelById.get(vendorId) ?? `Unknown (${vendorId.slice(0, 8)})`;

const formatExcelDate = (value: string) => (value ? format(parseISO(value), "M/d/yyyy") : "");

const currencyFormat = '_("$"* #,##0.00_);_("$"* \\(#,##0.00\\);_("$"* "-"??_);_(@_)';
const percentFormat = "0.0%";

/**
 * Aegle brand palette for Excel exports.
 *
 * These are ARGB hex strings required by ExcelJS — derived from the MUI theme
 * but hardcoded because ExcelJS cannot reference runtime theme values.
 *
 * Source values:
 *   primary: #2A7D99   secondary: #FEA30B   appBar: #368CAA
 *   text.primary: #1A1A1A   text.secondary: #6E6E6E
 */
const COLORS = {
  // Brand
  primary: "FF2A7D99",
  primaryDark: "FF1E5C73",
  secondary: "FFFEA30B",
  appBar: "FF368CAA",

  // Neutrals
  white: "FFFFFFFF",
  black: "FF1A1A1A",
  textSecondary: "FF6E6E6E",
  rowFillOdd: "FFF5F7FA",
  rowFillEven: "FFFFFFFF",
  subtotalFill: "FFE0EEF3",
  grandTotalFill: "FFFEF3D6",
  borderLight: "FFD5DAE0",
  borderMedium: "FFB0B8C2",
} as const;

const SHEET_OPTIONS: Partial<ExcelJS.AddWorksheetOptions> = {
  views: [{ showGridLines: false }],
};

// ==================== SHARED FONTS ====================

const labelFont: Partial<ExcelJS.Font> = { bold: true, size: 10, color: { argb: COLORS.black } };
const valueFont: Partial<ExcelJS.Font> = { size: 10, color: { argb: COLORS.textSecondary } };
const subtotalFont: Partial<ExcelJS.Font> = { bold: true, size: 10, color: { argb: COLORS.primaryDark } };

// ==================== SHARED HELPERS ====================

const thinBorder = (color: string = COLORS.borderLight): Partial<ExcelJS.Borders> => ({
  top: { style: "thin", color: { argb: color } },
  left: { style: "thin", color: { argb: color } },
  bottom: { style: "thin", color: { argb: color } },
  right: { style: "thin", color: { argb: color } },
});

const applyDataCellStyle = (cell: ExcelJS.Cell, fillColor: string, isCurrency = false) => {
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fillColor } };
  cell.border = thinBorder();
  cell.font = { size: 10, color: { argb: COLORS.black } };
  if (isCurrency) cell.numFmt = currencyFormat;
};

/** Alternating row fill for cleaner data tables */
const rowFill = (index: number) => (index % 2 === 0 ? COLORS.rowFillOdd : COLORS.rowFillEven);

const applyHeaderRow = (row: ExcelJS.Row, headers: string[]) => {
  row.height = 30;
  headers.forEach((header, idx) => {
    const cell = row.getCell(idx + 1);
    cell.value = header;
    cell.font = { bold: true, size: 10, color: { argb: COLORS.white } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.primary } };
    cell.border = thinBorder(COLORS.primaryDark);
  });
};

/**
 * Adds a labeled metadata row (e.g. "Policyholder:", "Policy Period:").
 * Used across the Summary sheet header and secondary sheet headers.
 */
const addMetadataRow = (
  sheet: ExcelJS.Worksheet,
  rowIdx: number,
  label: string,
  value: string,
  options?: {
    height?: number;
    valueFont?: Partial<ExcelJS.Font>;
    valueAlignment?: Partial<ExcelJS.Alignment>;
    valueBorder?: Partial<ExcelJS.Borders>;
  }
): void => {
  const row = sheet.getRow(rowIdx);
  row.height = options?.height ?? 28;
  row.getCell(1).value = label;
  row.getCell(1).font = labelFont;
  row.getCell(2).value = value;
  row.getCell(2).font = options?.valueFont ?? valueFont;
  if (options?.valueAlignment) row.getCell(2).alignment = options.valueAlignment;
  if (options?.valueBorder) row.getCell(2).border = options.valueBorder;
};

/**
 * Adds a compact branded header to secondary sheets (Claims Detail, Watch List, Tasks).
 * Returns the next available row index.
 */
const addSheetHeader = (
  sheet: ExcelJS.Worksheet,
  title: string,
  context: { policyholderName: string; policyPeriod: string; reportingPeriod: string },
  columnCount: number,
  /**
   * One-line note under the metadata explaining what this sheet's population IS, so a
   * reader who compares two sheets' totals and finds they differ can see it is by
   * design (#6825 review). Omitted sheets are unchanged.
   */
  caption?: string
): number => {
  let rowIdx = 1;

  // Title bar — primary brand color
  const titleRow = sheet.getRow(rowIdx++);
  titleRow.height = 32;
  titleRow.getCell(1).value = title;
  titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: COLORS.white } };
  titleRow.getCell(1).alignment = { vertical: "middle" };
  for (let col = 1; col <= columnCount; col++) {
    titleRow.getCell(col).fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.primary } };
  }

  // Metadata rows
  addMetadataRow(sheet, rowIdx++, "Policyholder:", context.policyholderName, { height: 20 });
  addMetadataRow(sheet, rowIdx++, "Policy Period:", context.policyPeriod, { height: 20 });
  addMetadataRow(sheet, rowIdx++, "Reporting Period:", context.reportingPeriod, { height: 20 });

  if (caption) {
    const captionRow = sheet.getRow(rowIdx++);
    captionRow.height = 16;
    captionRow.getCell(1).value = caption;
    captionRow.getCell(1).font = { italic: true, size: 9, color: { argb: COLORS.textSecondary } };
    captionRow.getCell(1).alignment = { vertical: "middle" };
    sheet.mergeCells(captionRow.number, 1, captionRow.number, Math.max(columnCount, 1));
  }

  // Spacer
  sheet.getRow(rowIdx++).height = 8;

  return rowIdx;
};

/**
 * Applies a totals row with the subtotal background and top border.
 */
const applyTotalsRowStyle = (row: ExcelJS.Row, columnCount: number) => {
  for (let col = 1; col <= columnCount; col++) {
    const cell = row.getCell(col);
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.subtotalFill } };
    cell.border = {
      top: { style: "medium", color: { argb: COLORS.primary } },
      bottom: { style: "thin", color: { argb: COLORS.borderMedium } },
      left: { style: "thin", color: { argb: COLORS.borderLight } },
      right: { style: "thin", color: { argb: COLORS.borderLight } },
    };
  }
};

// ==================== SHEET BUILDERS ====================

interface SheetContext {
  policy: Policy;
  client: Client;
  startDate: string;
  endDate: string;
  carrierVendor?: Vendor;
  tpaVendors: Vendor[];
  rxVendors: Vendor[];
  policyholderClient?: Client;
}

/**
 * Summary sheet — rich header layout with policy info, contacts, and full claimant data table.
 *
 * Column layout (index-resolved, #5326 — filed columns fan out per administering vendor):
 *   Claimant | Total YTD | Deductible |
 *   [Medical Filed — per TPA] | [RX Filed — per RX vendor] |
 *   Amount Owed (= SUM of all filed columns) | Amount Reimbursed | Denied |
 *   Variance (= Owed - Reimbursed - Denied) | Claimant Note
 *
 * Run-out vendors are labeled "(Run-out)"; claims with no vendor land in an
 * "Unassigned" column. Single-administrator policies render one filed column
 * (back-compat). Reimbursement/payment columns stay carrier-level, not segmented.
 */
const buildSummarySheet = (workbook: ExcelJS.Workbook, summaryRows: ClaimsSummaryRow[], ctx: SheetContext) => {
  const sheet = workbook.addWorksheet("Summary", SHEET_OPTIONS);
  const { policy, client, startDate, endDate, carrierVendor, tpaVendors, rxVendors, policyholderClient } = ctx;

  const primaryContact: ClientContact | undefined = client?.contacts?.[client?.primary_contact ?? 0];
  const policyDeductible = policy.specific_benefit?.specific_deductible || 0;

  const laseredClaimants = summaryRows.filter((row) => row.isLasered);
  const laserText =
    laseredClaimants.length > 0
      ? laseredClaimants.map((row) => `${row.name}: $${row.specDeductible.toLocaleString()}`).join(", ")
      : "None";

  // ---- Dynamic per-vendor filed columns (#5326, Option A) ----
  // Medical Filed fans out per administering TPA, RX Filed per RX vendor; run-out
  // vendors are labeled. Reimbursement/payment columns stay carrier-level.
  const vendorLabelById = buildVendorLabelMap(policy, tpaVendors, rxVendors);
  const labelFor = (key: string) =>
    key === UNASSIGNED_VENDOR_KEY ? "Unassigned" : resolveVendorName(vendorLabelById, key);
  const medicalCols = orderedVendorColumns(summaryRows, (r) => r.medicalFiledByVendor, policy.tpa_vendor_ids ?? []);
  const rxCols = orderedVendorColumns(summaryRows, (r) => r.rxFiledByVendor, policy.rx_vendor_ids ?? []);

  // Column index layout (index-resolved — no hardcoded letters):
  // Claimant | Total YTD | Deductible | [Medical Filed × TPA] | [RX Filed × Rx] |
  //   Amount Owed | Reimbursed | Denied | Variance | Note
  let colCursor = 1;
  const COL_CLAIMANT = colCursor++;
  const COL_YTD = colCursor++;
  const COL_DEDUCTIBLE = colCursor++;
  // ASD drawdown (#3302) — allocated only on aggregate-specific-deductible policies,
  // so a non-ASD workbook is byte-for-byte unchanged. Index-resolved via the cursor,
  // so every downstream column shifts automatically.
  const showAggSpec = isAggregateSpecificPolicy(policy);
  const COL_SPEC_EXCESS = showAggSpec ? colCursor++ : 0;
  const COL_ASD_CONSUMED = showAggSpec ? colCursor++ : 0;
  const COL_CARRIER_PAYABLE = showAggSpec ? colCursor++ : 0;
  const medColIdx = medicalCols.map(() => colCursor++);
  const rxColIdx = rxCols.map(() => colCursor++);
  const COL_OWED = colCursor++;
  const COL_REIMBURSED = colCursor++;
  const COL_DENIED = colCursor++;
  const COL_VARIANCE = colCursor++;
  const COL_NOTE = colCursor++;
  const totalCols = colCursor - 1;
  const filedColIdx = [...medColIdx, ...rxColIdx];

  const colWidths: number[] = new Array(totalCols).fill(20);
  colWidths[COL_CLAIMANT - 1] = 30;
  colWidths[COL_YTD - 1] = 22;
  colWidths[COL_DEDUCTIBLE - 1] = 14;
  colWidths[COL_NOTE - 1] = 34;
  sheet.columns = colWidths.map((width) => ({ width }));

  const centeredValueFont: Partial<ExcelJS.Font> = { ...valueFont };
  const centeredAlignment: Partial<ExcelJS.Alignment> = { horizontal: "center" };
  const underlineBorder: Partial<ExcelJS.Borders> = { bottom: { style: "thin", color: { argb: COLORS.borderMedium } } };

  let rowIdx = 1;

  // Row 1: Empty spacer
  sheet.getRow(rowIdx++).height = 10;

  // Row 2: Title bar — primary brand
  const titleRow = sheet.getRow(rowIdx++);
  titleRow.height = 44;
  titleRow.getCell(1).value = "Claims Summary";
  titleRow.getCell(1).font = { bold: true, size: 16, color: { argb: COLORS.white } };
  titleRow.getCell(1).alignment = { vertical: "middle" };
  for (let col = 1; col <= totalCols; col++) {
    titleRow.getCell(col).fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.primary } };
  }

  // Capture the first metadata row so the Policy Note section can be placed in the
  // top-right whitespace, aligned beside the policyholder block.
  const metadataStartRow = rowIdx;

  // Policyholder
  addMetadataRow(
    sheet,
    rowIdx++,
    "Policyholder:",
    policyholderClient?.client_legal_name || policy.source_data?.policyholder || "",
    {
      height: 36,
      valueFont: { bold: true, size: 11, color: { argb: COLORS.black } },
      valueAlignment: centeredAlignment,
      valueBorder: underlineBorder,
    }
  );

  // Primary Contact
  addMetadataRow(sheet, rowIdx++, "Primary Contact:", primaryContact?.contact_name || "", {
    height: 36,
    valueFont: centeredValueFont,
    valueAlignment: centeredAlignment,
    valueBorder: underlineBorder,
  });

  // Policy Period
  addMetadataRow(
    sheet,
    rowIdx++,
    "Policy Period:",
    `${formatExcelDate(policy.effective_start_date)} - ${formatExcelDate(policy.effective_end_date)}`,
    {
      valueFont: centeredValueFont,
      valueAlignment: centeredAlignment,
      valueBorder: underlineBorder,
    }
  );

  // Reporting Period
  addMetadataRow(sheet, rowIdx++, "Reporting Period:", `${formatExcelDate(startDate)} - ${formatExcelDate(endDate)}`, {
    valueFont: centeredValueFont,
    valueAlignment: centeredAlignment,
    valueBorder: underlineBorder,
  });

  // Population caption (#6825 review). This sheet is claimant-grain and populated by
  // spend-vs-deductible; Claims Detail is claim-grain and period-scoped. A reader who
  // compares the two totals and finds they differ needs to see that is by design, or it
  // reads as the reconciliation defect #6825 actually was.
  const populationCaptionRow = sheet.getRow(rowIdx++);
  populationCaptionRow.height = 16;
  populationCaptionRow.getCell(1).value =
    "One row per claimant at or over their specific deductible for the policy year. Dollar columns are scoped to the " +
    "reporting period; the population is not, so a claimant over their deductible with nothing filed in the period " +
    "appears at $0 owed. Totals are not expected to match the Claims Detail sheet, which is claim-grain.";
  populationCaptionRow.getCell(1).font = { italic: true, size: 9, color: { argb: COLORS.textSecondary } };
  populationCaptionRow.getCell(1).alignment = { vertical: "middle" };

  // Policy Note (US 5370) — its own section in the top-right whitespace, beside the
  // policyholder block and above Contacts (per the #5310 export follow-up). Written to
  // the existing metadata rows by number (not the rowIdx flow) so the left column is
  // untouched; the dark header aligns with "Policyholder:" and the body spans the rows
  // beside Primary Contact / Policy Period / Reporting Period. Only shown when present.
  if (policy.note) {
    const noteHeaderRow = sheet.getRow(metadataStartRow);
    noteHeaderRow.getCell(4).value = "Notes";
    noteHeaderRow.getCell(4).font = { bold: true, size: 11, color: { argb: COLORS.white } };
    // Match the Contacts section header width (cols D–F), not the full sheet width.
    for (let col = 4; col <= 6; col++) {
      noteHeaderRow.getCell(col).fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.primaryDark } };
    }

    sheet.mergeCells(metadataStartRow + 1, 4, metadataStartRow + 3, 6);
    const noteCell = sheet.getCell(metadataStartRow + 1, 4);
    noteCell.value = policy.note;
    noteCell.font = valueFont;
    noteCell.alignment = { wrapText: true, vertical: "top", horizontal: "left" };
  }

  // Spacer
  sheet.getRow(rowIdx++).height = 12;

  // Section headers — primary dark
  const sectionHeaderRow = sheet.getRow(rowIdx++);
  sectionHeaderRow.height = 24;
  sectionHeaderRow.getCell(1).value = "Policy Information";
  sectionHeaderRow.getCell(1).font = { bold: true, size: 11, color: { argb: COLORS.white } };
  for (let col = 1; col <= 2; col++) {
    sectionHeaderRow.getCell(col).fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.primaryDark } };
  }
  sectionHeaderRow.getCell(4).value = "Contacts";
  sectionHeaderRow.getCell(4).font = { bold: true, size: 11, color: { argb: COLORS.white } };
  for (let col = 4; col <= 6; col++) {
    sectionHeaderRow.getCell(col).fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.primaryDark } };
  }

  // Specific Deductible + SL Carrier
  const deductibleRow = sheet.getRow(rowIdx++);
  deductibleRow.height = 36;
  deductibleRow.getCell(1).value = "Specific Deductible:";
  deductibleRow.getCell(1).font = labelFont;
  deductibleRow.getCell(2).value = policyDeductible;
  deductibleRow.getCell(2).numFmt = currencyFormat;
  deductibleRow.getCell(2).font = valueFont;
  deductibleRow.getCell(4).value = "SL Carrier:";
  deductibleRow.getCell(4).font = labelFont;
  deductibleRow.getCell(5).value =
    carrierVendor?.claims_contact?.name || carrierVendor?.company_name || policy.source_data?.stop_loss_carrier || "";
  deductibleRow.getCell(5).font = valueFont;

  // Contract Basis + Rx Vendor
  const contractRow = sheet.getRow(rowIdx++);
  contractRow.height = 28;
  contractRow.getCell(1).value = "Contract Basis:";
  contractRow.getCell(1).font = labelFont;
  // #7320: the 999/12 paid-basis sentinel must never leave the system as a number.
  contractRow.getCell(2).value = policy.contract_basis ? displayContractBasis(policy.contract_basis) : "";
  contractRow.getCell(2).font = valueFont;
  contractRow.getCell(4).value = "Rx Vendor:";
  contractRow.getCell(4).font = labelFont;
  contractRow.getCell(5).value =
    rxVendors.map((v) => v.company_name).join(", ") || policy.specific_benefit?.rx_provider || "";
  contractRow.getCell(5).font = valueFont;

  // Lasers + Administrator
  const lasersRow = sheet.getRow(rowIdx++);
  lasersRow.height = 34;
  lasersRow.getCell(1).value = "Lasers:";
  lasersRow.getCell(1).font = labelFont;
  lasersRow.getCell(2).value = laserText;
  lasersRow.getCell(2).font = valueFont;
  lasersRow.getCell(4).value = "Administrator:";
  lasersRow.getCell(4).font = labelFont;
  lasersRow.getCell(5).value =
    tpaVendors.map((v) => v.company_name).join(", ") || policy.source_data?.third_party_administrator || "";
  lasersRow.getCell(5).font = valueFont;

  // SL Carrier row
  const carrierDetailRow = sheet.getRow(rowIdx++);
  carrierDetailRow.height = 28;
  carrierDetailRow.getCell(1).value = "SL Carrier:";
  carrierDetailRow.getCell(1).font = labelFont;
  carrierDetailRow.getCell(2).value = carrierVendor?.company_name || policy.source_data?.stop_loss_carrier || "";
  carrierDetailRow.getCell(2).font = valueFont;

  // Administrator row
  const administratorRow = sheet.getRow(rowIdx++);
  administratorRow.height = 28;
  administratorRow.getCell(1).value = "Administrator:";
  administratorRow.getCell(1).font = labelFont;
  administratorRow.getCell(2).value =
    tpaVendors.map((v) => v.company_name).join(", ") || policy.source_data?.third_party_administrator || "";
  administratorRow.getCell(2).font = valueFont;

  // Rx Vendor row
  const rxVendorRow = sheet.getRow(rowIdx++);
  rxVendorRow.height = 28;
  rxVendorRow.getCell(1).value = "Rx Vendor:";
  rxVendorRow.getCell(1).font = labelFont;
  rxVendorRow.getCell(2).value =
    rxVendors.map((v) => v.company_name).join(", ") || policy.specific_benefit?.rx_provider || "";
  rxVendorRow.getCell(2).font = valueFont;

  // Spacing
  for (let i = 0; i < 3; i++) sheet.getRow(rowIdx++).height = 10;

  // Reporting Through bar — appBar color
  const reportingThroughRow = sheet.getRow(rowIdx++);
  reportingThroughRow.height = 27;
  reportingThroughRow.getCell(1).value = "Reporting Through:";
  reportingThroughRow.getCell(1).font = { bold: true, size: 10, color: { argb: COLORS.white } };
  reportingThroughRow.getCell(2).value = formatExcelDate(endDate);
  reportingThroughRow.getCell(2).font = { size: 10, color: { argb: COLORS.white } };
  reportingThroughRow.getCell(3).value = "BUNDLED";
  reportingThroughRow.getCell(3).font = { size: 10, color: { argb: COLORS.white } };
  for (let col = 1; col <= totalCols; col++) {
    reportingThroughRow.getCell(col).fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.appBar } };
  }

  // Column headers — new layout matching UI data model
  const headerRow = sheet.getRow(rowIdx++);
  const headers: string[] = new Array(totalCols).fill("");
  headers[COL_CLAIMANT - 1] = "Claimant";
  headers[COL_YTD - 1] = "Total YTD\n(Full Policy Year Spend)";
  headers[COL_DEDUCTIBLE - 1] = "Deductible";
  if (showAggSpec) {
    headers[COL_SPEC_EXCESS - 1] = "Specific Excess";
    headers[COL_ASD_CONSUMED - 1] = "Applied to\nAgg Spec";
    headers[COL_CARRIER_PAYABLE - 1] = "Carrier\nPayable";
  }
  // Suppress the vendor suffix when a type has a single administering vendor, so
  // single-administrator policies keep the original bare "Medical Filed" / "RX Filed"
  // label (#5326 review — confirmed product decision). Fan out only when 2+ vendors.
  medicalCols.forEach((key, k) => {
    headers[medColIdx[k] - 1] = medicalCols.length === 1 ? "Medical Filed" : `Medical Filed\n— ${labelFor(key)}`;
  });
  rxCols.forEach((key, k) => {
    headers[rxColIdx[k] - 1] = rxCols.length === 1 ? "RX Filed" : `RX Filed\n— ${labelFor(key)}`;
  });
  headers[COL_OWED - 1] = "Amount Owed\nBy SL Carrier";
  headers[COL_REIMBURSED - 1] = "Amount Reimbursed\nby SL Carrier";
  headers[COL_DENIED - 1] = "Denied\nReimb";
  headers[COL_VARIANCE - 1] = "Variance";
  headers[COL_NOTE - 1] = "Claimant Note";
  applyHeaderRow(headerRow, headers);

  // Size the header row to its tallest label (#5326): long vendor names wrap past
  // a static height and clip. Estimate wrapped lines per header from each column's
  // character capacity (~width units ≈ chars at size 10), plus explicit newlines.
  // Set AFTER applyHeaderRow so it isn't reset by the shared helper's default.
  const headerLineCount = Math.max(
    2,
    ...headers.map((h, idx) => {
      const widthChars = Math.max(8, colWidths[idx] ?? 18);
      return h.split("\n").reduce((n, seg) => n + Math.max(1, Math.ceil(seg.length / widthChars)), 0);
    })
  );
  headerRow.height = Math.min(132, headerLineCount * 17 + 10);

  // Data rows — alternating fills
  const dataStartRow = rowIdx;

  summaryRows.forEach((claim, i) => {
    const dataRow = sheet.getRow(rowIdx++);
    dataRow.height = 26;
    const r = rowIdx - 1;
    const fill = rowFill(i);

    // Claimant
    const claimantDisplay = claim.isLasered ? `${claim.name} (Lasered)` : claim.name || "";
    dataRow.getCell(COL_CLAIMANT).value = claimantDisplay;
    applyDataCellStyle(dataRow.getCell(COL_CLAIMANT), fill);
    dataRow.getCell(COL_CLAIMANT).font = { bold: true, size: 10, color: { argb: COLORS.black } };

    // Total YTD (full policy-year health plan spend)
    dataRow.getCell(COL_YTD).value = claim.totalYTD || 0;
    applyDataCellStyle(dataRow.getCell(COL_YTD), fill, true);

    // Deductible
    dataRow.getCell(COL_DEDUCTIBLE).value = claim.specDeductible || 0;
    applyDataCellStyle(dataRow.getCell(COL_DEDUCTIBLE), fill, true);

    if (showAggSpec) {
      // An em dash where the engine produced no allocation — writing 0 would read as a
      // real zero and defeat the column. Currency styling only applies to numbers.
      const asdCells: [number, number | undefined][] = [
        [COL_SPEC_EXCESS, claim.specificExcess],
        [COL_ASD_CONSUMED, claim.asdConsumed],
        [COL_CARRIER_PAYABLE, claim.carrierPayable],
      ];
      for (const [col, value] of asdCells) {
        const cell = dataRow.getCell(col);
        cell.value = value === undefined ? "—" : value;
        applyDataCellStyle(cell, fill, value !== undefined);
      }
    }

    // Medical Filed per administering TPA + RX Filed per RX vendor (#5326)
    medicalCols.forEach((key, k) => {
      const cell = dataRow.getCell(medColIdx[k]);
      cell.value = claim.medicalFiledByVendor?.[key] || 0;
      applyDataCellStyle(cell, fill, true);
    });
    rxCols.forEach((key, k) => {
      const cell = dataRow.getCell(rxColIdx[k]);
      cell.value = claim.rxFiledByVendor?.[key] || 0;
      applyDataCellStyle(cell, fill, true);
    });

    // Amount Owed = sum of all filed columns (total filed with carrier)
    const owedCell = dataRow.getCell(COL_OWED);
    if (filedColIdx.length > 0) {
      const first = sheet.getCell(r, filedColIdx[0]).address;
      const last = sheet.getCell(r, filedColIdx[filedColIdx.length - 1]).address;
      owedCell.value = { formula: `SUM(${first}:${last})` };
    } else {
      owedCell.value = 0;
    }
    applyDataCellStyle(owedCell, fill, true);

    // Amount Reimbursed + Denied (carrier-level, not vendor-segmented)
    dataRow.getCell(COL_REIMBURSED).value = claim.totalReimbursed || 0;
    applyDataCellStyle(dataRow.getCell(COL_REIMBURSED), fill, true);
    dataRow.getCell(COL_DENIED).value = claim.totalDenied || 0;
    applyDataCellStyle(dataRow.getCell(COL_DENIED), fill, true);

    // Variance = Owed - Reimbursed - Denied
    const owedAddr = sheet.getCell(r, COL_OWED).address;
    const reimbAddr = sheet.getCell(r, COL_REIMBURSED).address;
    const deniedAddr = sheet.getCell(r, COL_DENIED).address;
    dataRow.getCell(COL_VARIANCE).value = { formula: `${owedAddr}-${reimbAddr}-${deniedAddr}` };
    applyDataCellStyle(dataRow.getCell(COL_VARIANCE), fill, true);

    // Claimant Note (policy-period-scoped, US 5273 / ADR-052 §10)
    dataRow.getCell(COL_NOTE).value = claim.claimantNote || "";
    applyDataCellStyle(dataRow.getCell(COL_NOTE), fill);
  });

  const dataEndRow = rowIdx - 1;

  // Columns to total: YTD + every filed column + owed/reimbursed/denied/variance.
  // Deductible is intentionally excluded (summing deductibles is not meaningful).
  const asdSumCols = showAggSpec ? [COL_SPEC_EXCESS, COL_ASD_CONSUMED, COL_CARRIER_PAYABLE] : [];
  const sumCols = [COL_YTD, ...asdSumCols, ...filedColIdx, COL_OWED, COL_REIMBURSED, COL_DENIED, COL_VARIANCE];

  // Empty spacer
  sheet.getRow(rowIdx++).height = 6;

  // Sub Totals — light brand tint
  const subTotalRow = sheet.getRow(rowIdx++);
  subTotalRow.height = 28;
  subTotalRow.getCell(1).value = "Sub Totals";
  subTotalRow.getCell(1).font = subtotalFont;
  applyTotalsRowStyle(subTotalRow, totalCols);

  for (const col of sumCols) {
    const cell = subTotalRow.getCell(col);
    cell.value = {
      formula: `SUM(${sheet.getCell(dataStartRow, col).address}:${sheet.getCell(dataEndRow, col).address})`,
    };
    cell.numFmt = currencyFormat;
    cell.font = subtotalFont;
  }

  // Empty spacer
  sheet.getRow(rowIdx++).height = 6;

  // Grand Total — secondary brand tint
  const grandTotalRow = sheet.getRow(rowIdx++);
  grandTotalRow.height = 28;
  grandTotalRow.getCell(1).value = "Grand Total";
  grandTotalRow.getCell(1).font = { bold: true, size: 11, color: { argb: COLORS.black } };

  for (let col = 1; col <= totalCols; col++) {
    const cell = grandTotalRow.getCell(col);
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.grandTotalFill } };
    cell.border = {
      top: { style: "medium", color: { argb: COLORS.secondary } },
      bottom: { style: "double", color: { argb: COLORS.secondary } },
      left: { style: "thin", color: { argb: COLORS.borderLight } },
      right: { style: "thin", color: { argb: COLORS.borderLight } },
    };
  }

  // Grand total mirrors sub-total for the same columns
  for (const col of sumCols) {
    const cell = grandTotalRow.getCell(col);
    cell.value = { formula: subTotalRow.getCell(col).address };
    cell.numFmt = currencyFormat;
    cell.font = { bold: true, size: 11, color: { argb: COLORS.black } };
  }
};

/**
 * Claims Detail sheet — stop-loss claims for breached claimants.
 */
// buildVendorLabelMap maps a vendor id to its display label for the exhibit,
// suffixing run-out administrators (#5325) e.g. "Acme TPA (Run-out)". Drawn from
// the policy's TPA + RX vendor lists; run-out flags come from the policy's
// {tpa,rx}_vendor_runout metadata.
const buildVendorLabelMap = (policy: Policy, tpaVendors: Vendor[], rxVendors: Vendor[]): Map<string, string> => {
  const runoutIds = new Set<string>();
  [...(policy.tpa_vendor_runout ?? []), ...(policy.rx_vendor_runout ?? [])].forEach((r) => {
    if (r.is_runout) runoutIds.add(r.vendor_id);
  });
  const map = new Map<string, string>();
  [...tpaVendors, ...rxVendors].forEach((v) => {
    map.set(v.id, runoutIds.has(v.id) ? `${v.company_name} (Run-out)` : v.company_name);
  });
  return map;
};

const buildClaimsDetailSheet = (
  workbook: ExcelJS.Workbook,
  stoplossClaims: StoplossClaim[],
  claimantNameById: Map<string, string>,
  headerCtx: { policyholderName: string; policyPeriod: string; reportingPeriod: string },
  vendorLabelById: Map<string, string>
) => {
  const sheet = workbook.addWorksheet("Claims Detail", SHEET_OPTIONS);
  const columnWidths = [24, 18, 14, 16, 16, 16, 16, 18, 18, 30, 22];
  sheet.columns = columnWidths.map((width) => ({ width }));

  let rowIdx = addSheetHeader(
    sheet,
    "Claims Detail",
    headerCtx,
    columnWidths.length,
    "Every stop-loss claim CREATED in the reporting period, listed whether or not its claimant appears on the Claims Summary. The two sheets answer different questions, so their totals are not expected to agree."
  );

  // Resolved from the full claimant set, not from summaryRows: #6828 decoupled the
  // Summary population from the claims listed here, so a Summary-derived map exported
  // "Unknown" for below-deductible claimants (review thread 27744).
  const nameMap = claimantNameById;

  const headers = [
    "Claimant",
    "Claim Type",
    "Status",
    // Created scopes the reporting period (#6823); Submitted is retained because the
    // carrier-submission date is still meaningful to the reader, it just no longer
    // decides which claims appear.
    "Created",
    "Submitted",
    "First Service",
    "Last Service",
    "Billed Amount",
    "Paid Amount",
    "Notes",
    "Administrator",
  ];
  applyHeaderRow(sheet.getRow(rowIdx++), headers);

  const dataStartRow = rowIdx;

  stoplossClaims.forEach((claim, i) => {
    const row = sheet.getRow(rowIdx++);
    row.height = 24;
    const fill = rowFill(i);

    row.getCell(1).value = nameMap.get(String(claim.claimant_id_ref)) ?? "Unknown";
    applyDataCellStyle(row.getCell(1), fill);

    row.getCell(2).value = claim.stoploss_claim_type?.toUpperCase() || "";
    applyDataCellStyle(row.getCell(2), fill);

    row.getCell(3).value = claim.claim_status || "";
    applyDataCellStyle(row.getCell(3), fill);

    // Scopes the reporting period (#6823), so it is the date that decided this row is
    // in the period. formatExcelDate resolves locally, matching the period bounds.
    row.getCell(4).value = claim.created_datetime ? formatExcelDate(claim.created_datetime) : "—";
    applyDataCellStyle(row.getCell(4), fill);

    row.getCell(5).value = claim.submitted_date ? formatExcelDate(claim.submitted_date) : "—";
    applyDataCellStyle(row.getCell(5), fill);

    row.getCell(6).value = claim.first_service_date ? formatExcelDate(claim.first_service_date) : "—";
    applyDataCellStyle(row.getCell(6), fill);

    row.getCell(7).value = claim.last_service_date ? formatExcelDate(claim.last_service_date) : "—";
    applyDataCellStyle(row.getCell(7), fill);

    row.getCell(8).value = claim.total_billed_amount ?? 0;
    applyDataCellStyle(row.getCell(8), fill, true);

    row.getCell(9).value = claim.total_paid_amount ?? 0;
    applyDataCellStyle(row.getCell(9), fill, true);

    row.getCell(10).value = claim.notes || "";
    applyDataCellStyle(row.getCell(10), fill);

    // Administering vendor (#5324/#5326): the TPA/RX that managed this claim,
    // run-out-suffixed. "Unassigned" when the claim carries no vendor.
    row.getCell(11).value = claim.vendor_id_ref
      ? resolveVendorName(vendorLabelById, claim.vendor_id_ref)
      : "Unassigned";
    applyDataCellStyle(row.getCell(11), fill);
  });

  const dataEndRow = rowIdx - 1;

  if (stoplossClaims.length > 0) {
    sheet.getRow(rowIdx++).height = 6;
    const totalsRow = sheet.getRow(rowIdx++);
    totalsRow.height = 28;
    totalsRow.getCell(1).value = "Totals";
    totalsRow.getCell(1).font = subtotalFont;
    applyTotalsRowStyle(totalsRow, columnWidths.length);

    for (const col of [8, 9]) {
      const cell = totalsRow.getCell(col);
      cell.value = {
        formula: `SUM(${sheet.getCell(dataStartRow, col).address}:${sheet.getCell(dataEndRow, col).address})`,
      };
      cell.numFmt = currencyFormat;
      cell.font = subtotalFont;
    }
  }
};

/**
 * Watch List sheet — claimants at 50–99% of their specific deductible.
 */
const buildWatchListSheet = (
  workbook: ExcelJS.Workbook,
  watchListRows: WatchListRow[],
  headerCtx: { policyholderName: string; policyPeriod: string; reportingPeriod: string }
) => {
  const sheet = workbook.addWorksheet("Watch List", SHEET_OPTIONS);
  const columnWidths = [24, 16, 18, 18, 16, 18, 18, 12];
  sheet.columns = columnWidths.map((width) => ({ width }));

  let rowIdx = addSheetHeader(sheet, "Watch List", headerCtx, columnWidths.length);

  const headers = [
    "Claimant",
    "Member ID",
    "Total YTD",
    "Deductible",
    "% of Deductible",
    "Remaining",
    "Status",
    "Lasered",
  ];
  applyHeaderRow(sheet.getRow(rowIdx++), headers);

  const dataStartRow = rowIdx;

  watchListRows.forEach((row, i) => {
    const dataRow = sheet.getRow(rowIdx++);
    dataRow.height = 24;
    const fill = rowFill(i);

    dataRow.getCell(1).value = row.name;
    applyDataCellStyle(dataRow.getCell(1), fill);

    dataRow.getCell(2).value = row.memberId || "—";
    applyDataCellStyle(dataRow.getCell(2), fill);

    dataRow.getCell(3).value = row.totalYTD;
    applyDataCellStyle(dataRow.getCell(3), fill, true);

    dataRow.getCell(4).value = row.specDeductible;
    applyDataCellStyle(dataRow.getCell(4), fill, true);

    dataRow.getCell(5).value = row.percentOfDeductible / 100;
    applyDataCellStyle(dataRow.getCell(5), fill);
    dataRow.getCell(5).numFmt = percentFormat;

    dataRow.getCell(6).value = row.remaining;
    applyDataCellStyle(dataRow.getCell(6), fill, true);

    dataRow.getCell(7).value = row.threshold;
    applyDataCellStyle(dataRow.getCell(7), fill);

    dataRow.getCell(8).value = row.isLasered ? "Yes" : "";
    applyDataCellStyle(dataRow.getCell(8), fill);
  });

  const dataEndRow = rowIdx - 1;

  if (watchListRows.length > 0) {
    sheet.getRow(rowIdx++).height = 6;
    const totalsRow = sheet.getRow(rowIdx++);
    totalsRow.height = 28;
    totalsRow.getCell(1).value = `${watchListRows.length} Claimant${watchListRows.length === 1 ? "" : "s"}`;
    totalsRow.getCell(1).font = subtotalFont;
    applyTotalsRowStyle(totalsRow, columnWidths.length);

    const ytdCell = totalsRow.getCell(3);
    ytdCell.value = {
      formula: `SUM(${sheet.getCell(dataStartRow, 3).address}:${sheet.getCell(dataEndRow, 3).address})`,
    };
    ytdCell.numFmt = currencyFormat;
    ytdCell.font = subtotalFont;
  }
};

/**
 * Tasks sheet — tasks flagged for the claims summary.
 */
const buildTasksSheet = (
  workbook: ExcelJS.Workbook,
  tasks: Task[],
  usersMap: Record<string, User>,
  headerCtx: { policyholderName: string; policyPeriod: string; reportingPeriod: string }
) => {
  const sheet = workbook.addWorksheet("Tasks", SHEET_OPTIONS);
  const columnWidths = [28, 36, 14, 14, 20, 24, 16];
  sheet.columns = columnWidths.map((width) => ({ width }));

  let rowIdx = addSheetHeader(sheet, "Tasks", headerCtx, columnWidths.length);

  const headers = ["Title", "Description", "Status", "Due Date", "Assigned To", "Related To", "Last Modified"];
  applyHeaderRow(sheet.getRow(rowIdx++), headers);

  const formatObjectType = (objectType: string) => {
    const typeMap: Record<string, string> = {
      clients: "Client",
      policies: "Policy",
      claimants: "Claimant",
      stoploss_claims: "Claim",
      "stoploss-claims": "Claim",
      reimbursements: "Reimbursement",
      documents: "Document",
    };
    return typeMap[objectType] || objectType;
  };

  tasks.forEach((task, i) => {
    const assignedUser = task.assigned_user_id_ref ? usersMap[task.assigned_user_id_ref] : undefined;
    const row = sheet.getRow(rowIdx++);
    row.height = 24;
    const fill = rowFill(i);

    row.getCell(1).value = task.title || "";
    applyDataCellStyle(row.getCell(1), fill);

    row.getCell(2).value = task.description || "";
    applyDataCellStyle(row.getCell(2), fill);

    row.getCell(3).value = task.status || "";
    applyDataCellStyle(row.getCell(3), fill);

    row.getCell(4).value = task.due_date ? formatExcelDate(task.due_date) : "—";
    applyDataCellStyle(row.getCell(4), fill);

    row.getCell(5).value = assignedUser?.full_name || "—";
    applyDataCellStyle(row.getCell(5), fill);

    row.getCell(6).value =
      task.record?.label && task.record?.object_type
        ? `${formatObjectType(task.record.object_type)}: ${task.record.label}`
        : "—";
    applyDataCellStyle(row.getCell(6), fill);

    row.getCell(7).value = task.modified_datetime ? formatExcelDate(task.modified_datetime) : "—";
    applyDataCellStyle(row.getCell(7), fill);
  });
};

/**
 * Reimbursement sheet (#5310) — one row per reimbursement: claimant, reimbursement
 * date, amount, payment type (Check/ACH/EFT/Wire), and the Aegle Accel funded date.
 *
 * The funded date is claim-grain (per US 5330): it lives on the funding-request
 * stop-loss claim (aura_disbursed_at), resolved here via reimbursement.stoploss_claim_id_ref
 * → fundedDateByClaimId. A reimbursement whose claim hasn't been funded shows "—".
 */
const buildReimbursementSheet = (
  workbook: ExcelJS.Workbook,
  reimbursements: Reimbursement[],
  claimantNameById: Map<string, string>,
  fundedDateByClaimId: Record<string, string>,
  headerCtx: { policyholderName: string; policyPeriod: string; reportingPeriod: string }
) => {
  const sheet = workbook.addWorksheet("Reimbursements", SHEET_OPTIONS);
  const columnWidths = [28, 18, 16, 12, 20];
  sheet.columns = columnWidths.map((width) => ({ width }));

  let rowIdx = addSheetHeader(sheet, "Reimbursements", headerCtx, columnWidths.length);

  // Resolved from the full claimant set, not from summaryRows: #6828 decoupled the
  // Summary population from the claims listed here, so a Summary-derived map exported
  // "Unknown" for below-deductible claimants (review thread 27744).
  const nameMap = claimantNameById;

  const headers = ["Claimant", "Reimbursement Date", "Amount", "Type", "Aegle Accel Funded Date"];
  applyHeaderRow(sheet.getRow(rowIdx++), headers);

  const dataStartRow = rowIdx;

  reimbursements.forEach((r, i) => {
    const row = sheet.getRow(rowIdx++);
    row.height = 24;
    const fill = rowFill(i);

    row.getCell(1).value = nameMap.get(String(r.claimant_id_ref)) ?? "Unknown";
    applyDataCellStyle(row.getCell(1), fill);

    // Reimbursement date: the report's one precedence, payment ?? check ?? received (#7560).
    const reimbDate = reimbursementDate(r);
    row.getCell(2).value = reimbDate ? formatExcelDate(reimbDate) : "—";
    applyDataCellStyle(row.getCell(2), fill);

    row.getCell(3).value = r.reimbursement_amount ?? 0;
    applyDataCellStyle(row.getCell(3), fill, true);

    row.getCell(4).value = paymentTypeLabel(r);
    applyDataCellStyle(row.getCell(4), fill);

    const fundedDate = r.stoploss_claim_id_ref ? fundedDateByClaimId[String(r.stoploss_claim_id_ref)] : undefined;
    row.getCell(5).value = fundedDate ? formatExcelDate(fundedDate) : "—";
    applyDataCellStyle(row.getCell(5), fill);
  });

  const dataEndRow = rowIdx - 1;

  if (reimbursements.length > 0) {
    sheet.getRow(rowIdx++).height = 6;
    const totalsRow = sheet.getRow(rowIdx++);
    totalsRow.height = 28;
    totalsRow.getCell(1).value = `${reimbursements.length} Reimbursement${reimbursements.length === 1 ? "" : "s"}`;
    totalsRow.getCell(1).font = subtotalFont;
    applyTotalsRowStyle(totalsRow, columnWidths.length);

    const amtCell = totalsRow.getCell(3);
    amtCell.value = {
      formula: `SUM(${sheet.getCell(dataStartRow, 3).address}:${sheet.getCell(dataEndRow, 3).address})`,
    };
    amtCell.numFmt = currencyFormat;
    amtCell.font = subtotalFont;
  }
};

// ==================== MAIN EXPORT FUNCTION ====================

export interface DownloadClaimsSummaryParams {
  policy: Policy;
  client: Client;
  options: ExportOptions;
  summaryRows: ClaimsSummaryRow[];
  /** claimant_id_ref -> claimant_name over the full fetched claimant set (#6828). */
  claimantNameById: Map<string, string>;
  watchListRows: WatchListRow[];
  stoplossClaims: StoplossClaim[];
  reimbursements?: Reimbursement[];
  /** stoploss_claim_id_ref → Aegle Accel funded date (aura_disbursed_at) */
  fundedDateByClaimId?: Record<string, string>;
  tasks: Task[];
  startDate: string;
  endDate: string;
  carrierVendor?: Vendor;
  tpaVendors?: Vendor[];
  rxVendors?: Vendor[];
  policyholderClient?: Client;
  usersMap?: Record<string, User>;
}

export const downloadClaimsSummary = async ({
  policy,
  client,
  options,
  summaryRows,
  claimantNameById,
  watchListRows,
  stoplossClaims,
  reimbursements = [],
  fundedDateByClaimId = {},
  tasks,
  startDate,
  endDate,
  carrierVendor,
  tpaVendors = [],
  rxVendors = [],
  policyholderClient,
  usersMap = {},
}: DownloadClaimsSummaryParams) => {
  const workbook = new ExcelJS.Workbook();

  const sheetCtx: SheetContext = {
    policy,
    client,
    startDate,
    endDate,
    carrierVendor,
    tpaVendors,
    rxVendors,
    policyholderClient,
  };

  const headerCtx = {
    policyholderName: policyholderClient?.client_legal_name || policy.source_data?.policyholder || "",
    policyPeriod: `${formatExcelDate(policy.effective_start_date)} - ${formatExcelDate(policy.effective_end_date)}`,
    reportingPeriod: `${formatExcelDate(startDate)} - ${formatExcelDate(endDate)}`,
  };

  if (options.sheets.summary) {
    buildSummarySheet(workbook, summaryRows, sheetCtx);
  }

  if (options.sheets.claimsDetail && stoplossClaims.length > 0) {
    const vendorLabelById = buildVendorLabelMap(policy, tpaVendors, rxVendors);
    buildClaimsDetailSheet(workbook, stoplossClaims, claimantNameById, headerCtx, vendorLabelById);
  }

  if (options.sheets.watchList && watchListRows.length > 0) {
    buildWatchListSheet(workbook, watchListRows, headerCtx);
  }

  // Scope reimbursements to the claimants shown ANYWHERE on the exhibit — the union of
  // the Summary population and the Claims Detail population. Those were the same set
  // before #6828; scoping to summaryRows alone now silently drops the reimbursements of
  // a below-deductible claimant whose claim IS listed (review thread 27745).
  const exhibitClaimantIds = new Set<string>([
    ...summaryRows.map((row) => row.id),
    ...stoplossClaims.map((claim) => String(claim.claimant_id_ref)),
  ]);
  const exhibitReimbursements = reimbursements.filter((r) => exhibitClaimantIds.has(String(r.claimant_id_ref)));

  if (options.sheets.reimbursement && exhibitReimbursements.length > 0) {
    buildReimbursementSheet(workbook, exhibitReimbursements, claimantNameById, fundedDateByClaimId, headerCtx);
  }

  if (options.sheets.tasks && tasks.length > 0) {
    buildTasksSheet(workbook, tasks, usersMap, headerCtx);
  }

  // Guard: an xlsx with zero worksheets is invalid and will corrupt the file
  if (workbook.worksheets.length === 0) {
    const fallback = workbook.addWorksheet("Summary", SHEET_OPTIONS);
    fallback.getRow(1).getCell(1).value = "No data available for the selected reporting period.";
    fallback.getRow(1).getCell(1).font = valueFont;
    fallback.columns = [{ width: 50 }];
  }

  // Export
  const buffer = await workbook.xlsx.writeBuffer();

  const policyholderName = (policyholderClient?.client_legal_name || policy.source_data?.policyholder || "Unknown")
    .replace(/[^a-zA-Z0-9]/g, "_")
    .replace(/_+/g, "_")
    .substring(0, 30);

  const policyYear = policy.effective_start_date?.slice(0, 4) ?? new Date().getFullYear().toString();
  const formattedStartDate = startDate.replace(/-/g, "");
  const formattedEndDate = endDate.replace(/-/g, "");

  const filename = `Claims_Summary_${policyholderName}_${policyYear}_${formattedStartDate}_${formattedEndDate}.xlsx`;

  saveAs(new Blob([buffer]), filename);
};
